import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Attendance,
  PastClass,
  RecordingState,
  getPastClasses,
  requestRecordingAccess,
} from '../../api/learner';
import { KidEmpty } from '../../../../design-system/kid';
import { useLearnerSpace } from '../../learner/LearnerSpace';
import { formatDay, useLearnerTimeZone, zoneLabel } from '../../learner/learnerTime';

type Load = { status: 'loading' } | { status: 'error' } | { status: 'ready'; classes: PastClass[] };

const ATTENDANCE_WORDS: Record<Attendance, string> = {
  PRESENT: 'Fuiste a esta clase',
  LATE: 'Llegaste un poco tarde',
  // STU-13-FR-04: what happened, without blame.
  NO_SHOW: 'Te perdiste esta clase',
  UNKNOWN: 'Todavía no sabemos si fuiste',
};

/**
 * STU-15-FR-03 — every recording state the server can give, in honest words.
 * None of them is an error the learner caused.
 */
const RECORDING_WORDS: Record<Exclude<RecordingState, 'AVAILABLE'>, string> = {
  PROCESSING: 'La grabación se está preparando. Vuelve más tarde.',
  NOT_RELEASED: 'La grabación todavía no está lista para verla.',
  RESTRICTED: 'Esta grabación no está disponible.',
  MEDIA_UNAVAILABLE: 'Las grabaciones no están disponibles por ahora.',
  EXPIRED: 'Esta grabación ya no está disponible.',
  NOT_ENTITLED: 'Solo puedes ver las clases a las que fuiste.',
  PLAYBACK_NOT_GRANTED: 'Tu familia no ha activado ver las grabaciones.',
  NOT_RECORDED: 'Esta clase no se grabó.',
  FAILED: 'No pudimos guardar esta grabación.',
};

/** DEC-S3-S12-01 — a class that was not held, as what it was. Nobody's fault. */
const NOT_HELD_WORDS: Partial<Record<PastClass['outcome'], string>> = {
  CANCELLED: 'Esta clase se canceló',
  RESCHEDULED: 'Esta clase se movió a otro día',
  NOT_DELIVERED: 'Esta clase no se dio',
};

const keyOf = (c: PastClass) => `${c.enrollmentId}#${c.seq}`;

/**
 * UC-S12 — "Clases pasadas" (STU-14-FR-01..05, STU-15-FR-03).
 *
 * The learner's own history, from the server's scoped read: course, class
 * number, day and attendance. A recording is played only when the server
 * says it is available, and each press of "Ver" asks for a fresh,
 * short-lived link (STU-15-FR-05) that goes straight into the player — it is
 * never shown, stored or kept after the player closes. A recording that is
 * processing, expired or not this learner's says so and leaves the rest of
 * the history in place (STEP-07). No payment history lives here (FR-06).
 */
const PastClasses: React.FC = () => {
  const { studentId } = useLearnerSpace();
  const tz = useLearnerTimeZone(studentId);
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [playing, setPlaying] = useState<{ key: string; url: string } | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [playError, setPlayError] = useState<string | null>(null);

  const fetchPast = useCallback(() => {
    if (!studentId) return;
    setLoad({ status: 'loading' });
    getPastClasses(studentId)
      .then((res) => setLoad({ status: 'ready', classes: res.classes }))
      .catch(() => setLoad({ status: 'error' }));
  }, [studentId]);

  useEffect(fetchPast, [fetchPast]);

  const play = async (c: PastClass) => {
    const key = keyOf(c);
    setOpening(key);
    setPlayError(null);
    setPlaying(null);
    try {
      const { url } = await requestRecordingAccess(c.enrollmentId, c.seq, studentId ?? undefined);
      setPlaying({ key, url });
    } catch {
      setPlayError(key);
    } finally {
      setOpening(null);
    }
  };

  return (
    <>
      <h1 className="hi">Clases pasadas</h1>
      <p className="hi-s">Clases que ya diste. Puedes volver a verlas si fuiste.</p>

      {load.status === 'loading' && (
        <p className="hi-s" role="status">
          Cargando tus clases pasadas…
        </p>
      )}

      {load.status === 'error' && (
        <div className="lrn-status" role="alert">
          <h2>No pudimos cargar tus clases pasadas</h2>
          <p>No es culpa tuya. Prueba otra vez en un momento.</p>
          <button className="ag-b" onClick={fetchPast}>
            Intentar otra vez
          </button>
        </div>
      )}

      {load.status === 'ready' && load.classes.length === 0 && (
        <KidEmpty
          icon="🕘"
          title="Aún no hay clases pasadas"
          detail="Cuando termines una, aparecerá aquí."
        />
      )}

      {load.status === 'ready' && load.classes.length > 0 && (
        <>
          <p className="lrn-zone">{zoneLabel(tz)}</p>
          <p className="lrn-zone">
            Grabar o compartir la cara o el nombre de otro alumno está prohibido.
          </p>
          {load.classes.map((c) => {
            const key = keyOf(c);
            const isPlaying = playing?.key === key;
            return (
              <section
                className="lrn-status"
                key={key}
                aria-label={`${c.courseTitle}, clase ${c.seq}`}
              >
                <h2>
                  {c.courseTitle} · clase {c.seq}
                </h2>
                {/* STU-14-FR-02 — the teacher of that class, as they were then. */}
                {c.teacherName && <p>Con tu profesor: {c.teacherName}</p>}
                <p>
                  {formatDay(c.sessionEndedAt || c.scheduledStart || '', tz)} ·{' '}
                  {NOT_HELD_WORDS[c.outcome] ??
                    ATTENDANCE_WORDS[c.attendance] ??
                    ATTENDANCE_WORDS.UNKNOWN}
                </p>
                {c.outcome === 'PARTIAL' && <p>Esta clase duró menos de lo normal.</p>}

                {c.suggestedPractice && (
                  // MC-02 — a suggestion from the teacher, nothing more: no
                  // button, no date, no grade.
                  <div className="note" aria-label={`Para practicar después de la clase ${c.seq}`}>
                    <p>
                      <strong>Para practicar, si quieres: </strong>
                      {c.suggestedPractice.text}
                    </p>
                    <p className="lrn-zone">
                      {c.suggestedPractice.estimatedMinutes
                        ? `Unos ${c.suggestedPractice.estimatedMinutes} minutos. `
                        : ''}
                      No tienes que entregar nada y no tiene nota.
                    </p>
                  </div>
                )}

                {c.recording.state === 'AVAILABLE' ? (
                  <>
                    {c.recording.availableUntil && (
                      <p className="lrn-zone">
                        Puedes verla hasta el {formatDay(c.recording.availableUntil, tz)}.
                      </p>
                    )}
                    {isPlaying ? (
                      <>
                        {/* The link lives only in this element, for this viewing. */}
                        <video
                          className="lrn-video"
                          src={playing.url}
                          controls
                          controlsList="nodownload"
                          disablePictureInPicture
                          onContextMenu={(e) => e.preventDefault()}
                          aria-label={`Grabación de ${c.courseTitle}, clase ${c.seq}`}
                          autoPlay
                        />
                        <button className="ag-b soft" onClick={() => setPlaying(null)}>
                          Cerrar
                        </button>
                      </>
                    ) : (
                      <button
                        className="ag-b"
                        onClick={() => play(c)}
                        disabled={opening === key}
                        aria-label={`Ver la clase ${c.seq} de ${c.courseTitle}`}
                      >
                        {opening === key ? 'Abriendo…' : 'Ver la clase'}
                      </button>
                    )}
                    {playError === key && (
                      <p role="alert">No pudimos abrir la grabación. Inténtalo otra vez.</p>
                    )}
                  </>
                ) : NOT_HELD_WORDS[c.outcome] ? null : (
                  <p>{RECORDING_WORDS[c.recording.state] ?? RECORDING_WORDS.FAILED}</p>
                )}

                <Link
                  className="lrn-link"
                  to={`/student/courses/${encodeURIComponent(c.courseId)}`}
                >
                  De qué trata
                </Link>
              </section>
            );
          })}
        </>
      )}
    </>
  );
};

export default PastClasses;
